import { useSnackbars } from '@customerio/pluma-components/react';
import { useDeleteCollection, useUpdateCollection } from '@/services/db/collection/mutations';
import { importIsInvalid, type Collection } from '@/services/db/collection/entity';
import { buildClearErrorRequest } from './collection-payload';

/**
 * Non-destructive dismissal of an import error: a `clear_error` update that leaves the
 * collection and its data in place. This is all the collection detail page does — it is
 * rendering the collection, so discarding it there would strand the user on a deleted id.
 */
export function useClearCollectionImportError(workspaceId: number) {
  const update = useUpdateCollection({ workspaceId });
  const snackbars = useSnackbars();

  return async (collection: Collection) => {
    try {
      await update.mutateAsync({ id: collection.id, update: buildClearErrorRequest() });
    } catch {
      snackbars.create({ variant: 'critical', message: "The error couldn't be dismissed. Try again." });
    }
  };
}

type ImportErrorRecoveryOptions = {
  collection: Collection;
  workspaceId: number;
  canDelete: boolean;
  /** Restarts from the add flow once a discarded stub is gone. */
  onDiscarded: ((collection: Collection) => void) | undefined;
  /** Re-imports into the existing collection, for anything that still holds data. */
  onReimport: () => void;
};

/**
 * Import-error recovery for the collections index. A first import that produced no rows
 * leaves a collection the user never got to use, so retry and dismiss both discard that
 * stub — the row vanishing on list invalidation is the feedback, which is why the index is
 * the only surface that discards. Without delete permission, or while something still
 * references the collection, both fall back to re-importing into it rather than dead-ending
 * on a delete the user isn't allowed to make.
 */
export function useCollectionImportErrorRecovery({
  collection,
  workspaceId,
  canDelete,
  onDiscarded,
  onReimport,
}: ImportErrorRecoveryOptions) {
  const deleteCollection = useDeleteCollection({ workspaceId });
  const clearImportError = useClearCollectionImportError(workspaceId);
  const snackbars = useSnackbars();

  const canDiscardStub = importIsInvalid(collection) && canDelete && !collection.in_use;

  const discardStub = async () => {
    try {
      await deleteCollection.mutateAsync({ id: collection.id });
      return true;
    } catch {
      snackbars.create({ variant: 'critical', message: "The collection couldn't be discarded. Try again." });
      return false;
    }
  };

  const tryAgain = async () => {
    if (!canDiscardStub) {
      onReimport();
      return;
    }

    if (await discardStub()) {
      onDiscarded?.(collection);
    }
  };

  const dismiss = async () => {
    if (canDiscardStub) {
      await discardStub();
      return;
    }

    await clearImportError(collection);
  };

  return {
    onTryAgain: () => void tryAgain(),
    onDismiss: () => void dismiss(),
  };
}
